import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form } from 'antd';
import { RcFile, UploadFile as UploadFileAntd } from 'antd/es/upload';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { useFileEncryptionUpload } from '@/entities/file/hooks/use-file-encryption-upload';
import { useInterwillsActions, useIwExecutionStore } from '@/entities/interwills';
import { useParticipantActions } from '@/entities/interwills/hooks/useParticipantActions';
import { useApi } from '@/shared/api';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { DatePicker } from '@/shared/uikit/DatePicker';
import { UploadFile } from '@/shared/uikitV2/UploadFile';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { WitnessVerificationModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

type FormParams = {
  signingDate: string;
  witnessVerification?: UploadFileAntd<RcFile>;
};

export const WitnessVerificationModal: FC<WitnessVerificationModalProps> = () => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'witnessVerificationInfo' });
  const modals = useModalsStore();
  const { encryptAndUploadFile } = useFileEncryptionUpload();
  const { getInterwill } = useInterwillsActions();
  const { witnessDocuments, setWitnessDocuments } = useIwExecutionStore();
  const { getCurrentWitness } = useParticipantActions();
  const witnessId = getCurrentWitness()?.id as DtoId;
  const api = useApi();

  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
  } = useForm<FormParams>();

  const onSubmit = async (values: FormParams) => {
    const interwill = await getInterwill();
    if (!interwill?.centralDbInterwillId) return;
    const res = await api.profilesGet();
    const additionalPublicKey = res.data?.mainUser?.publicKey ?? null;

    const file = values.witnessVerification?.originFileObj;
    const uploadedFile = file
      ? await encryptAndUploadFile(file, {
          publicKey: additionalPublicKey,
          itemData: { isFileAttached: true },
        })
      : null;
    if (!uploadedFile) return;

    const currentDocument = witnessDocuments[witnessId];
    const { data } = currentDocument
      ? await api.willsUpdateAttachment(interwill.centralDbInterwillId, currentDocument.id, {
          filePath: uploadedFile,
        })
      : await api.willsAddAttachment(interwill.centralDbInterwillId, {
          type: 'DOCUMENT',
          filePath: uploadedFile,
        });

    if (!data) return;

    setWitnessDocuments({
      ...witnessDocuments,
      [witnessId]: {
        ...data,
        filePath: data.filePath ?? '',
        signingDate: values.signingDate,
        name: values.witnessVerification?.name ?? '',
        size: values.witnessVerification?.size ?? 0,
      },
    });
    modals.closeModal();
  };

  return (
    <ModalContainerV1 title={t('title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(onSubmit)}>
        <InnerForm>
          <Controller
            name="witnessVerification"
            control={control}
            render={({ field }) => {
              return (
                <UploadFile
                  maxCount={1}
                  onChange={(data) => {
                    field.onChange(data.file);
                  }}
                  accept="application/pdf"
                  title={t('uploadButton')}
                  description={t('uploadDescription')}
                  listType="picture"
                />
              );
            }}
          />
          <Controller
            name="signingDate"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState }) => {
              return (
                <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                  <Form.Item
                    validateStatus={fieldState.error ? 'error' : ''}
                    help={fieldState.error?.message}
                  >
                    <DatePicker
                      placeholder={t(`fields.${field.name}.placeholder`)}
                      type={'text'}
                      {...field}
                    />
                  </Form.Item>
                </FieldContainerIw>
              );
            }}
          />
        </InnerForm>
        <Buttons>
          <ButtonIw loading={isSubmitting} disabled={isSubmitting} htmlType="submit">
            {t('btn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: auto;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
`;
